import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, useMediaQuery, Alert } from '@mui/material';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import { mapStyles } from '../mapStyles';
import { convertAddressToCoordinates } from '../APIs/action';
import {
  formatCompleteAddress,
  validatePhoneNumber,
} from '../../../utils/settings';
import { toast } from 'react-toastify';
import {
  editAddressAction,
  saveAddressAction,
} from '../../../v3/MyAccount/Address/APIs/action';
import { CustomButton, TextField } from '../../MUI_custom_component';
import { useSelector } from 'react-redux';
class GoogleMapsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Error caught by boundary
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='body1'
            color='text.secondary'
          >
            Map temporarily unavailable
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            Please refresh the page
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}
const AddressConfirmationModal = ({
  addressData = {},
  coordinates = {},
  onConfirm,
  onBack,
  isAddressBeingEdited = false,
  non_reward = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [map, setMap] = useState(null);
  const [error, setError] = useState({});
  const [alternateNumber, setAlternateNumber] = useState(
    addressData.phone_no || null,
  );
  const { selectedCompany } = useSelector((state) => ({
    selectedCompany: state.AddressReducer?.selectedCompany,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [mapCoordinates, setMapCoordinates] = useState({
    lat: null,
    lng: null,
  });

  useEffect(() => {
    if (coordinates && coordinates.lat && coordinates.lng) {
      setMapCoordinates({
        lat: Number(coordinates.lat),
        lng: Number(coordinates.lng),
      });
    } else {
      // Use default coordinates for display only
      setMapCoordinates({
        lat: 28.7041, // Default to Delhi
        lng: 77.1025,
      });

      // If no coordinates provided, convert address to coordinates
      const formattedAddress = formatCompleteAddress(addressData);
      if (formattedAddress) {
        getCoordinatesFromAddress(formattedAddress);
      }
    }
  }, []);
  useEffect(() => {}, [mapCoordinates]);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });
  const {
    address_line_1 = '',
    address_line_2 = '',
    district_name = '',
    pincode_id = '',
    state_name = '',
    location_name = '',
  } = addressData;

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      map
    ) {
      try {
        setMap(null);
      } catch (error) {
        setMap(null);
      }
    } else {
      setMap(null);
    }
  }, []);
  const validateWhatsappNumber = (number) => {
    const isRewardsAddress =
      !non_reward || addressData.address_type === 'REWARDS';

    if (isRewardsAddress) {
      if (!number || number.trim() === '') {
        return 'WhatsApp number is required for Rewards addresses';
      }
      if (!validatePhoneNumber(number)) {
        return 'Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9';
      }
    }
    return '';
  };
  // useEffect(() => {
  //   const errorMessage = validateWhatsappNumber(alternateNumber);
  //   setWhatsappError(errorMessage);
  // }, [addressData.address_type, alternateNumber, non_reward]);

  const handleConfirm = async () => {
    setIsLoading(true);

    // Validate WhatsApp number before proceeding with API call
    const currentValidationError = validateWhatsappNumber(alternateNumber);
    if (currentValidationError) {
      setWhatsappError(currentValidationError);
      setIsLoading(false);
      return toast.error(currentValidationError);
    }
    // Check if there are any WhatsApp validation errors
    if (whatsappError) {
      setIsLoading(false);
      return toast.error(whatsappError);
    }
    let payload = {
      address_line_1: addressData.address_line_1 || null,
      address_line_2: addressData.address_line_2 || null,
      state_id: addressData.state_id || null,
      state_name: addressData.state_name || null,
      district_id: addressData.district_id || null,
      district_name: addressData.district_name || null,
      location_id: addressData.location_id || null,
      location_name: addressData.location_name || null,
      pincode_id: addressData.pincode_id || null,
      address_tag: non_reward ? addressData.address_type || null : 'REWARDS',
      address_tag_value: addressData.address_type_other || null,
      landmark: addressData.landmark || null,
      whatsapp_no: alternateNumber || null,
      geo_coordinates: addressData.geo_coordinates || coordinates || null,
      company_esi_id:
        !non_reward || addressData.address_type === 'REWARDS'
          ? addressData.company_esi_id
          : null,
      company_id: selectedCompany?.company_id || null,
    };
    let response = {};
    if (isAddressBeingEdited) {
      payload = {
        ...payload,
        address_id: addressData.id || addressData.address_id,
      };
      response = await editAddressAction(payload);
    } else {
      response = await saveAddressAction(payload);
    }
    setIsLoading(false);
    if (response.error) {
      setIsLoading(false);
      toast.error(response.message);
      return;
    } else {
      toast.success(response.message);
      onConfirm({
        ...addressData,
        coordinates: coordinates,
        alternateNumber: alternateNumber,
      });
    }
  };

  const handleBack = () => {
    onBack();
  };

  const getCoordinatesFromAddress = async (address) => {
    if (!address || address.trim().length === 0) {
      return;
    }

    try {
      const response = await convertAddressToCoordinates(address);
      const { data, error } = response;
      if (error) {
        console.error('Error converting address to coordinates:', error);
        return null;
      }

      if (data && data.latitude && data.longitude) {
        const newCoordinates = {
          lat: Number(data.latitude),
          lng: Number(data.longitude),
        };

        setMapCoordinates(newCoordinates);
      }
    } catch (e) {
      console.error('Error:', e);
      return null;
    }
  };
  return (
    <Box
      sx={{
        height: isMobile ? '100vh' : '589px',
        width: isMobile ? '100%' : '618px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMobile ? 0 : '12px',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: '200px',
        }}
      >
        {loadError ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Alert
              severity='error'
              sx={{ maxWidth: '400px' }}
            >
              {loadError?.message || 'Failed to load Google Maps'}
            </Alert>
          </Box>
        ) : !isLoaded ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Typography>Loading map...</Typography>
          </Box>
        ) : (
          <GoogleMapsErrorBoundary>
            <GoogleMap
              key='address-confirmation-map'
              mapContainerStyle={{
                height: '100%',
                width: '100%',
                borderTopLeftRadius: isMobile ? 0 : '12px',
                borderTopRightRadius: isMobile ? 0 : '12px',
              }}
              center={{ lat: mapCoordinates.lat, lng: mapCoordinates.lng }}
              zoom={15}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                styles: mapStyles,
                disableDefaultUI: true,
                zoomControl: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                gestureHandling: 'none',
              }}
            >
              {mapCoordinates && mapCoordinates.lat && mapCoordinates.lng && (
                <Marker
                  position={{
                    lat: mapCoordinates.lat,
                    lng: mapCoordinates.lng,
                  }}
                  draggable={false}
                  icon={
                    window.google && window.google.maps
                      ? {
                          url:
                            'data:image/svg+xml;charset=UTF-8,' +
                            encodeURIComponent(`
                                        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                                          <circle cx="60" cy="60" r="50" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                                          <path d="M60 40c-5.52 0-10 4.48-10 10 0 7.5 10 20 10 20s10-12.5 10-20c0-5.52-4.48-10-10-10zm0 13.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="#333333"/>
                                        </svg>
                                      `),
                          scaledSize: new window.google.maps.Size(120, 120),
                          anchor: new window.google.maps.Point(60, 75),
                        }
                      : undefined
                  }
                />
              )}
            </GoogleMap>
          </GoogleMapsErrorBoundary>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: 'white',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',

          overflowY: 'auto',
          borderBottomLeftRadius: isMobile ? 0 : '12px',
          borderBottomRightRadius: isMobile ? 0 : '12px',
          height: non_reward ? '272px' : '350px',
          marginTop: '-20px',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant='h1'
            color='#333333'
          >
            {non_reward ? 'Confirm Address' : 'Confirm Reward Address'}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant='labelD'>Firm Name</Typography>
            <Typography variant='body1'>
              {selectedCompany?.company_name}
            </Typography>
          </Box>
          <Box sx={{display:'flex',flexDirection:'column', gap: .5}}>
            {non_reward && addressData?.address_type_other && (
              <Typography variant='labelD'>{addressData?.address_type_other }</Typography>
            )}
             <Box>
            <Typography variant='p1'>
              {[address_line_1, address_line_2].filter(Boolean).join(', ')}
            </Typography>
            <Typography variant='p1'>
              {[location_name, district_name, state_name, pincode_id]
                .filter(Boolean)
                .join(', ')}
            </Typography>
          </Box>
          </Box>
         

          {!non_reward && (
            <Box sx={{ mt: '16px' }}>
              <TextField
                label={`Whatsapp Number${!non_reward || addressData.address_type === 'REWARDS' ? '*' : ''}`}
                variant='standard'
                fullWidth
                required={!non_reward || addressData.address_type === 'REWARDS'}
                placeholder='Type Whatsapp number here'
                inputProps={{
                  maxLength: 10,
                  inputMode: 'numeric',
                }}
                value={alternateNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/[^0-9]/g, '');
                  setAlternateNumber(numericValue);
                  const errorMessage = validateWhatsappNumber(numericValue);
                  setWhatsappError(errorMessage);
                }}
                error={!!whatsappError}
                helperText={whatsappError}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: '32px' }}>
          <CustomButton
            variant='outlined'
            color='primary'
            label='No, Change Address'
            onClick={handleBack}
            disabled={isLoading}
            fullWidth
          />
          <CustomButton
            variant='filled'
            color='primary'
            label={isLoading ? 'Saving...' : 'Yes'}
            onClick={handleConfirm}
            disabled={isLoading || !!whatsappError}
            fullWidth
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddressConfirmationModal;
